/* eslint-disable no-bitwise, max-classes-per-file, no-console, no-unused-vars, class-methods-use-this, no-trailing-spaces */
/* no-restricted-syntax, import/prefer-default-export, no-max-len */
import loglevel from 'loglevel';
import { yealink } from './yealinkSDK';

const MODULE_NAME = 'yealink/manager';
const logger = loglevel.getLogger(MODULE_NAME);
logger.setLevel('info');

/**
 * Observable-like implementation for subscribing to events
 */
class YealinkObservable {
  constructor() {
    this.subscribers = [];
  }

  subscribe(callback) {
    this.subscribers.push(callback);
    return {
      unsubscribe: () => {
        this.subscribers = this.subscribers.filter((sub) => sub !== callback);
      },
    };
  }

  next(value) {
    this.subscribers.forEach((callback) => {
      try {
        callback(value);
      } catch (error) {
        logger.error(`${MODULE_NAME} - Observable callback error:`, error);
      }
    });
  }
}

/**
 * YealinkManager provides a unified interface for Yealink headsets
 * compatible with the existing Jabra headset management system
 */
class YealinkManager {
  constructor() {
    this.deviceAdded = new YealinkObservable();
    this.deviceRemoved = new YealinkObservable();
    this.initialized = false;
    this.connectedDevices = [];
    this.activeCallControl = null; // Track the active call control for message routing

    this.initMessageListener();
    this.initHidListeners();
  }

  /**
   * Initialize the manager - compatible with Jabra's init function
   */
  async init() {
    if (this.initialized) {
      logger.info(`${MODULE_NAME} - Already initialized`);
      return this;
    }

    try {
      logger.info(`${MODULE_NAME} - Initializing Yealink manager`);

      // Get existing devices
      await yealink.getDevices;

      this.initialized = true;
      logger.info(`${MODULE_NAME} - Initialization complete`);

      return this;
    } catch (error) {
      logger.error(`${MODULE_NAME} - Initialization failed:`, error);
      throw error;
    }
  }

  /**
   * Initialize message listener for device events
   */
  initMessageListener() {
    window.addEventListener('message', (event) => {
      if (event.data.direction === 'yealink-headset-extension-from-web-hid') {
        console.log('YealinkManager received message:', event.data); //eslint-disable-line
        this.handleYealinkMessage(event.data);
      }
    });
  }

  /**
   * Initialize HID connect/disconnect listeners
   */
  initHidListeners() {
    // HID listeners are already set up in yealinkSDK.js
    // We just need to track devices through the message system
  }

  /**
   * Handle messages from Yealink SDK
   */
  handleYealinkMessage(data) {
    switch (data.Msg) {
      case 'deviceConnect':
        this.handleDeviceConnect(data);
        break;
      case 'deviceDisconnect':
        this.handleDeviceDisconnect(data);
        break;
      default:
        // Route call control messages to the active call control instance
        if (this.activeCallControl) {
          this.activeCallControl.handleCallControlMessage(data);
        }
        break;
    }
  }

  /**
   * Handle device connection
   */
  handleDeviceConnect(data) {
    const device = {
      id: { equals: (other) => other.pid === data.pid && other.vid === data.vid },
      productName: data.productName,
      name: data.productName,
      vendorId: data.vid,
      productId: data.pid,
      isYealinkDevice: true,
    };

    this.connectedDevices.push(device);
    this.deviceAdded.next(device);

    logger.info(`${MODULE_NAME} - Device connected:`, device);
  }

  /**
   * Handle device disconnection
   */
  handleDeviceDisconnect(data) {
    const deviceIndex = this.connectedDevices.findIndex(
      (device) => device.productId.toString() === data.deviceId.replace(/^\d+/, ''),
    );

    if (deviceIndex >= 0) {
      const device = this.connectedDevices[deviceIndex];
      this.connectedDevices.splice(deviceIndex, 1);
      this.deviceRemoved.next(device);

      logger.info(`${MODULE_NAME} - Device disconnected:`, device);
    }
  }

  /**
   * Check if device supports call control - all Yealink devices do
   */
  supportsEasyCallControl(device) {
    return device.isYealinkDevice === true;
  }

  /**
   * Request device pairing - compatible with Jabra webHidPairing
   * Must be called directly in user gesture context
   */
  async requestDevice() {
    try {
      // Call the native WebHID API directly to preserve user gesture
      const filters = [{
        usage: 0x0005,
        usagePage: 0x000B,
        vendorId: 0x6993,
      }];

      const devices = await navigator.hid.requestDevice({ filters });
      const yealinkDevices = [];

      devices.forEach((device) => {
        if (device.vendorId === 0x6993) {
          yealinkDevices.push({
            id: { equals: (other) => other.vendorId === device.vendorId && other.productId === device.productId },
            productName: device.productName,
            name: device.productName,
            vendorId: device.vendorId,
            productId: device.productId,
            isYealinkDevice: true,
            originalDevice: device,
          });
        }
      });

      logger.info(`${MODULE_NAME} - requestDevice - Found ${yealinkDevices.length} Yealink devices`);
      return yealinkDevices;
    } catch (error) {
      logger.error(`${MODULE_NAME} - Device request failed:`, error);
      throw error;
    }
  }

  /**
   * Get all connected devices
   */
  getConnectedDevices() {
    return this.connectedDevices;
  }

  /**
   * Set the active call control for message routing
   */
  setActiveCallControl(callControl) {
    this.activeCallControl = callControl;
    logger.info(`${MODULE_NAME} - Active call control set for device:`, callControl?.device?.productName);
  }
}

/**
 * YealinkCallControl provides call control functionality
 * compatible with Jabra's EasyCallControl interface
 */
class YealinkCallControl {
  constructor(device) {
    this.device = device;
    this.name = device.productName; // Add name property for getName() function
    this.muteState = new YealinkObservable();
    this.holdState = new YealinkObservable();
    this.swapRequest = new YealinkObservable();
    this.ongoingCalls = new YealinkObservable();
    this.onDisconnect = new YealinkObservable();

    this.callCount = 0;
    this.isMuted = false;
    this.isOnHold = false;

    // Don't add a new message listener for each call control instance
    // The YealinkManager already handles messages globally
    // this.initMessageListener();

    // Store setup promise for awaiting
    this.setupPromise = this.setupDevice().catch((error) => {
      logger.error(`${MODULE_NAME} - Setup device error:`, error);
      throw error;
    });
  }

  /**
   * Setup the device and message handling
   */
  async setupDevice() {
    // Add small delay to let WebHID system stabilize
    await new Promise((resolve) => {
      setTimeout(resolve, 100);
    });

    logger.info(`${MODULE_NAME} - setupDevice - Adding device to call device list:`, this.device.productName);
    // eslint-disable-next-line no-console
    console.log('[setupDevice] Looking for:', this.device.productName, this.device.vendorId, this.device.productId);

    // Wait for device to be available in SDK's allDevices array
    let retryCount = 0;
    const maxRetries = 20; // 2 seconds max wait

    while (retryCount < maxRetries) {
      const allDevices = yealink.getAllDevices();
      // eslint-disable-next-line no-console
      console.log('[setupDevice] Retry', retryCount, '- allDevices:', allDevices.length);
      allDevices.forEach((dev, idx) => {
        // eslint-disable-next-line no-console
        console.log('[setupDevice] Dev', idx, ':', dev.device?.productName, dev.device?.vendorId);
      });

      const deviceFound = allDevices.find((dev) => (
        dev.device.vendorId === this.device.vendorId
        && dev.device.productId === this.device.productId
      ));
      
      if (deviceFound) {
        logger.info(`${MODULE_NAME} - setupDevice - Device found in SDK after ${retryCount * 100}ms:`, this.device.productName);

        // Add the device to call device list
        yealink.addCallDevice(
          this.device.productId.toString(),
          this.device.vendorId,
          this.device.productId,
        );

        // Then set it as active (which may try to open it)
        logger.info(`${MODULE_NAME} - setupDevice - Setting active device:`, this.device.productId.toString());
        yealink.setActiveDevice(this.device.productId.toString());

        // Verify the device was set as active
        const activeDevice = yealink.getActiveDevice();
        if (activeDevice) {
          logger.info(`${MODULE_NAME} - setupDevice - Active device confirmed:`, activeDevice.productName);

          // Explicitly wait for device to be opened (setActiveDevice calls openDevice async)
          logger.info(`${MODULE_NAME} - setupDevice - Waiting for device to open...`);
          // eslint-disable-next-line no-await-in-loop
          const opened = await yealink.openDevice();
          logger.info(`${MODULE_NAME} - setupDevice - Device opened:`, opened);
        } else {
          logger.error(`${MODULE_NAME} - setupDevice - Failed to set active device`);
        }
        return;
      }
      
      retryCount += 1;
      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => {
        setTimeout(resolve, 100);
      });
    }
    
    logger.error(`${MODULE_NAME} - setupDevice - Device not found in SDK after ${maxRetries * 100}ms:`, this.device.productName);
  }

  /**
   * Initialize message listener for call control events
   */
  initMessageListener() {
    window.addEventListener('message', (event) => {
      if (event.data.direction === 'yealink-headset-extension-from-web-hid') {
        this.handleCallControlMessage(event.data);
      }
    });
  }

  /**
   * Handle call control messages from Yealink SDK
   */
  handleCallControlMessage(data) {
    switch (data.Msg) {
      case 'onHook':
        yealink.onHook(); // this is needed
        this.handleEndCall();
        break;
      case 'offHook':
        yealink.offHook(); // this is needed
        this.handleStartCall();
        break;
      case 'Mute':
        this.mute(); // this is needed
        this.isMuted = true;
        this.muteState.next('muted');
        break;
      case 'Unmute':
        this.unmute(); // this is needed
        this.isMuted = false;
        this.muteState.next('unmuted');
        break;
      case 'Hold':
        this.hold(); // this is needed
        this.isOnHold = true;
        this.holdState.next('on-hold');
        break;
      case 'Resume':
        this.resume(); // this is needed
        this.isOnHold = false;
        this.holdState.next('not-on-hold');
        break;
      case 'Reject':
        this.handleEndCall();
        break;
      case 'Redial':
        this.redial(); // this is needed
        break;
      default:
        break;
    }
  }

  /**
   * Handle start call event
   */
  handleStartCall() {
    this.callCount = Math.max(1, this.callCount);
    yealink.updateCallStatus({ callActive: true });
    this.ongoingCalls.next(this.callCount);
  }

  /**
   * Handle end call event
   */
  handleEndCall() {
    this.callCount = 0;
    this.isMuted = false;
    this.isOnHold = false;
    yealink.updateCallStatus({
      callActive: false,
      muteStatus: false,
      holdStatus: false,
    });
    this.ongoingCalls.next(this.callCount);
  }

  /**
   * Start a call - compatible with Jabra interface
   */
  startCall() {
    try {
      const success = yealink.offHook();
      if (success) {
        this.callCount += 1;
        this.ongoingCalls.next(this.callCount);
      }
      return success;
    } catch (error) {
      logger.error(`${MODULE_NAME} - Start call failed:`, error);
      return false;
    }
  }

  /**
   * End a call - compatible with Jabra interface
   */
  endCall() {
    try {
      const success = yealink.onHook();
      if (success && this.callCount > 0) {
        this.callCount -= 1;
        this.ongoingCalls.next(this.callCount);
      }
      return success;
    } catch (error) {
      logger.error(`${MODULE_NAME} - End call failed:`, error);
      return false;
    }
  }

  /**
   * Ring the device
   */
  ring() {
    return yealink.ring();
  }

  /**
   * Stop ringing
   */
  ringOff() {
    return yealink.ringoff();
  }

  /**
   * Mute the device
   */
  mute() {
    const success = yealink.mute();
    if (success) {
      this.isMuted = true;
    }
    return success;
  }

  /**
   * Unmute the device
   */
  unmute() {
    const success = yealink.unmute();
    if (success) {
      this.isMuted = false;
    }
    return success;
  }

  /**
   * Hold the call
   */
  hold() {
    const success = yealink.hold();
    if (success) {
      this.isOnHold = true;
    }
    return success;
  }

  /**
   * Resume the call
   */
  resume() {
    const success = yealink.resume();
    if (success) {
      this.isOnHold = false;
    }
    return success;
  }

  /**
   * Get current call count
   */
  getCallCount() {
    return this.callCount;
  }

  /**
   * Get mute status
   */
  getMuteStatus() {
    return this.isMuted;
  }

  /**
   * Get hold status
   */
  getHoldStatus() {
    return this.isOnHold;
  }

  redial() {
    console.log('Redial action triggered from YealinkManager');
    // The actual redial message is now sent directly from yealinkSDK.js
    // when bit 16 is detected in handleInputReport
  }
}

/**
 * Factory class for creating Yealink call controls
 * Compatible with Jabra's EasyCallControlFactory
 */
class YealinkCallControlFactory {
  constructor(manager) {
    this.manager = manager;
  }

  /**
   * Check if device supports call control
   */
  supportsEasyCallControl(device) {
    return device.isYealinkDevice === true;
  }

  /**
   * Create multi-call control for device
   */
  async createMultiCallControl(device) {
    if (!this.supportsEasyCallControl(device)) {
      throw new Error('Device does not support Yealink call control');
    }

    const callControl = new YealinkCallControl(device);

    // Wait for device setup to complete before returning
    await callControl.setupPromise;

    // Register this call control with the manager for message routing
    this.manager.setActiveCallControl(callControl);

    logger.info(`${MODULE_NAME} - createMultiCallControl - Device setup completed for:`, device.productName);

    return callControl;
  }
}

// Export the main classes and functions
export { YealinkManager, YealinkCallControl, YealinkCallControlFactory };

// Default export for easy initialization
export default YealinkManager;
